/**
 * Touch controls for phones and tablets (landscape). Layout:
 *  - left thumb: a floating joystick anywhere in the lower-left zone (walk);
 *  - right edge: the eight elements in two columns, plus Area / Self / Staff /
 *    Magick / Clear and a Spellbook toggle;
 *  - everywhere else on the field: touch and hold to cast forward toward the
 *    finger; drag to steer a beam or spray; lift to stop or to throw a charged
 *    boulder.
 * Everything goes through Controls' virtual input, so prediction, the HUD
 * preview and the server see exactly what a keyboard and mouse would send.
 * Pointer Events with pointerType 'touch' / 'pen'; mice keep the desktop path.
 */

import type { BaseElement, Element } from '@shared/index.js';
import type { Controls } from './controls.js';
import type { BindAction } from './keymap.js';
import { ELEMENT_SWATCH } from '../palette.js';
import { stashPodsLayout } from '../render/hudLayout.js';
import { hudScale } from '../render/hud.js';
import { drawElementGlyph, drawElementIcon, iconsReady } from '../render/glyphs.js';

/** Joystick radius in CSS px; a full push is this far from where the thumb landed. */
const STICK_R = 56;
const DEADZONE = 0.15;

/** Two columns, keyboard order down each (Q W E R | A S D F) so the hand shape carries over. */
const ELEMENT_COLUMNS: readonly (readonly BaseElement[])[] = [
  ['water', 'life', 'shield', 'cold'],
  ['lightning', 'arcane', 'earth', 'fire'],
];

const ACTION_BUTTONS: readonly { action: BindAction; label: string }[] = [
  { action: 'area', label: 'Area' },
  { action: 'self', label: 'Self' },
  { action: 'weapon', label: 'Staff' },
  { action: 'magick', label: 'Magick' },
  { action: 'weave', label: 'Weave' },
  { action: 'clear', label: 'Clear' },
];

/** Extra finger room round the stash pods (CSS px each side): the pods are small, fingers are not. */
const POD_SLOP = 10;

/** True when the primary pointer is a finger (phones, most tablets). */
export function prefersTouch(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

export class TouchControls {
  readonly root: HTMLElement;
  private readonly stickZone: HTMLElement;
  private readonly stickBase: HTMLElement;
  private readonly stickKnob: HTMLElement;
  private stickId: number | null = null;
  private stickX = 0;
  private stickY = 0;
  private castId: number | null = null;
  private visible = false;
  private readonly off: (() => void)[] = [];

  constructor(private readonly canvas: HTMLCanvasElement, private readonly controls: Controls, parent: HTMLElement, onBook: () => void) {
    this.root = div('touch-ui');
    this.root.hidden = true;

    // --- joystick zone (lower left)
    this.stickZone = div('touch-stick-zone');
    this.stickBase = div('touch-stick-base');
    this.stickKnob = div('touch-stick-knob');
    this.stickBase.appendChild(this.stickKnob);
    this.stickBase.hidden = true;
    this.stickZone.appendChild(this.stickBase);
    this.root.appendChild(this.stickZone);
    this.listen(this.stickZone, 'pointerdown', (e) => this.stickDown(e));
    this.listen(window, 'pointermove', (e) => this.stickMove(e));
    this.listen(window, 'pointerup', (e) => this.stickUp(e));
    this.listen(window, 'pointercancel', (e) => this.stickUp(e));

    // --- elements + actions (right edge)
    const pad = div('touch-pad');
    const actions = div('touch-actions');
    for (const b of ACTION_BUTTONS) actions.appendChild(this.actionButton(b.action, b.label));
    const book = this.button('Book', 'touch-btn touch-book');
    this.listen(book, 'pointerdown', (e) => {
      e.preventDefault();
      onBook();
    });
    actions.appendChild(book);
    pad.appendChild(actions);
    const grid = div('touch-elements');
    for (const col of ELEMENT_COLUMNS) {
      const c = div('touch-el-col');
      for (const el of col) c.appendChild(this.elementButton(el));
      grid.appendChild(c);
    }
    pad.appendChild(grid);
    this.root.appendChild(pad);

    // --- cast: touch and hold on the field (the canvas)
    this.listen(canvas, 'pointerdown', (e) => this.castDown(e));
    this.listen(window, 'pointermove', (e) => this.castMove(e));
    this.listen(window, 'pointerup', (e) => this.castUp(e));
    this.listen(window, 'pointercancel', (e) => this.castUp(e));

    parent.appendChild(this.root);
  }

  /** Shown only in a room on a touch device; hiding releases anything held. */
  setVisible(on: boolean): void {
    if (this.visible === on) return;
    this.visible = on;
    this.root.hidden = !on;
    if (!on) this.releaseAll();
  }

  dispose(): void {
    this.releaseAll();
    for (const f of this.off) f();
    this.root.remove();
  }

  // ---------------------------------------------------------------- joystick

  private stickDown(e: PointerEvent): void {
    if (!isFinger(e) || this.stickId !== null) return;
    e.preventDefault();
    this.stickId = e.pointerId;
    this.stickX = e.clientX;
    this.stickY = e.clientY;
    this.stickBase.hidden = false;
    this.stickBase.style.left = `${e.clientX}px`;
    this.stickBase.style.top = `${e.clientY}px`;
    this.stickKnob.style.transform = 'translate(-50%, -50%)';
  }

  private stickMove(e: PointerEvent): void {
    if (e.pointerId !== this.stickId) return;
    e.preventDefault();
    let dx = (e.clientX - this.stickX) / STICK_R, dy = (e.clientY - this.stickY) / STICK_R;
    const len = Math.hypot(dx, dy);
    if (len > 1) { dx /= len; dy /= len; }
    this.stickKnob.style.transform = `translate(calc(-50% + ${dx * STICK_R}px), calc(-50% + ${dy * STICK_R}px))`;
    this.controls.setVirtualMove(len < DEADZONE ? null : { x: dx, y: dy });
  }

  private stickUp(e: PointerEvent): void {
    if (e.pointerId !== this.stickId) return;
    this.stickId = null;
    this.stickBase.hidden = true;
    this.controls.setVirtualMove(null);
  }

  // ---------------------------------------------------------------- casting on the field

  private castDown(e: PointerEvent): void {
    if (!isFinger(e)) return;
    // The stash pods are drawn on the field's canvas; a tap on one is a stash tap, not a cast.
    const pod = this.podAt(e);
    if (pod >= 0) {
      e.preventDefault();
      const src = `pod-${pod}`;
      this.controls.pressVirtual(pod === 0 ? 'stash1' : 'stash2', src);
      this.controls.releaseVirtual(src);
      return;
    }
    if (this.castId !== null) return;
    e.preventDefault();
    this.castId = e.pointerId;
    this.controls.setPointerScreen(e.clientX, e.clientY);
    // Aim is sampled in Controls.update; push the press one frame later so the cast carries this finger's angle.
    requestAnimationFrame(() => { if (this.castId === e.pointerId) this.controls.pressVirtual('force', 'cast'); });
  }

  /** Which stash pod (0 or 1) a finger landed on, or -1. */
  private podAt(e: PointerEvent): number {
    const r = this.canvas.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const pods = stashPodsLayout(r.width, r.height, hudScale(r.height));
    return pods.findIndex((p) => x >= p.x - POD_SLOP && x <= p.x + p.size + POD_SLOP && y >= p.y - POD_SLOP && y <= p.y + p.size + POD_SLOP);
  }

  private castMove(e: PointerEvent): void {
    if (e.pointerId !== this.castId) return;
    e.preventDefault();
    this.controls.setPointerScreen(e.clientX, e.clientY);
  }

  private castUp(e: PointerEvent): void {
    if (e.pointerId !== this.castId) return;
    this.castId = null;
    this.controls.releaseVirtual('cast');
  }

  // ---------------------------------------------------------------- buttons

  private elementButton(el: BaseElement): HTMLElement {
    const b = this.button('', `touch-btn touch-el el-${el}`);
    b.style.setProperty('--el', ELEMENT_SWATCH[el].core);
    b.style.setProperty('--el-dark', ELEMENT_SWATCH[el].dark);
    b.appendChild(glyphCanvas(el));
    b.setAttribute('aria-label', el);
    this.listen(b, 'pointerdown', (e) => {
      e.preventDefault();
      b.classList.add('pressed');
      this.controls.pressVirtual(el, `el-${el}`);
    });
    const up = (): void => { b.classList.remove('pressed'); this.controls.releaseVirtual(`el-${el}`); };
    this.listen(b, 'pointerup', up);
    this.listen(b, 'pointercancel', up);
    this.listen(b, 'pointerleave', up);
    return b;
  }

  private actionButton(action: BindAction, label: string): HTMLElement {
    const b = this.button(label, `touch-btn touch-act act-${action}`);
    this.listen(b, 'pointerdown', (e) => {
      e.preventDefault();
      b.classList.add('pressed');
      this.controls.pressVirtual(action, `act-${action}`);
    });
    const up = (): void => { b.classList.remove('pressed'); this.controls.releaseVirtual(`act-${action}`); };
    this.listen(b, 'pointerup', up);
    this.listen(b, 'pointercancel', up);
    this.listen(b, 'pointerleave', up);
    return b;
  }

  private button(label: string, cls: string): HTMLElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = label;
    // Buttons never take focus or open a long-press menu.
    b.tabIndex = -1;
    this.listen(b, 'contextmenu', (e) => e.preventDefault());
    return b;
  }

  private releaseAll(): void {
    if (this.castId !== null) { this.castId = null; this.controls.releaseVirtual('cast'); }
    if (this.stickId !== null) { this.stickId = null; this.stickBase.hidden = true; }
    this.controls.setVirtualMove(null);
  }

  private listen(target: EventTarget, type: string, fn: (e: PointerEvent) => void): void {
    const h = fn as unknown as EventListener;
    target.addEventListener(type, h, { passive: false });
    this.off.push(() => target.removeEventListener(type, h));
  }
}

function isFinger(e: PointerEvent): boolean {
  return e.pointerType === 'touch' || e.pointerType === 'pen';
}

function div(cls: string): HTMLElement {
  const d = document.createElement('div');
  d.className = cls;
  return d;
}

function glyphCanvas(el: Element): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const px = 36, dpr = Math.min(3, window.devicePixelRatio || 1);
  c.width = px * dpr;
  c.height = px * dpr;
  c.style.width = `${px}px`;
  c.style.height = `${px}px`;
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.scale(dpr, dpr);
    ctx.fillStyle = ELEMENT_SWATCH[el].dark;
    drawElementGlyph(ctx, el, px / 2, px / 2, px * 0.62);
    // once the art library's icons land, the button shows its pixel icon on a dark medallion
    let tries = 0;
    const swap = (): void => {
      if (!iconsReady()) {
        if (++tries < 120) window.setTimeout(swap, 500);
        return;
      }
      ctx.clearRect(0, 0, px, px);
      ctx.fillStyle = 'rgba(16,18,26,0.82)';
      ctx.beginPath();
      ctx.arc(px / 2, px / 2, px / 2, 0, Math.PI * 2);
      ctx.fill();
      drawElementIcon(ctx, el, px / 2, px / 2, px * 0.66);
    };
    swap();
  }
  return c;
}

/**
 * Full screen and no page movement on touch devices: no scroll, no bounce,
 * no pinch or double-tap zoom, no long-press menus. The first tap also asks
 * for real fullscreen and landscape lock where the browser allows it
 * (Android Chrome; iPhone Safari needs "Add to Home Screen" instead).
 */
export function lockTouchPage(): () => void {
  // Panels with their own scroll (lobby, spellbook, settings, landing, results) may still scroll inside;
  // everything else is pinned.
  const SCROLLERS = '.lobby-panel, .book-content, .settings-list, .landing-panel, .results-panel';
  const stop = (e: Event): void => {
    const t = e.target;
    if (e.type === 'touchmove' && t instanceof Element && t.closest(SCROLLERS)) return;
    if (e.cancelable) e.preventDefault();
  };
  const opts: AddEventListenerOptions = { passive: false };
  document.addEventListener('touchmove', stop, opts);
  document.addEventListener('gesturestart', stop, opts);
  document.addEventListener('dblclick', stop, opts);
  const goFull = (): void => {
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
      req?.({ navigationUI: 'hide' } as FullscreenOptions)
        ?.then(() => (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape'))
        .catch(() => { /* not allowed here; the page is still locked in place */ });
    }
  };
  document.addEventListener('pointerdown', goFull, { once: true });
  return () => {
    document.removeEventListener('touchmove', stop);
    document.removeEventListener('gesturestart', stop);
    document.removeEventListener('dblclick', stop);
    document.removeEventListener('pointerdown', goFull);
  };
}
